/**
 * Offline POS sale queue — the page side.
 *
 * A sale rung up without a connection is stored on the device and replayed when
 * the network returns. Every queued sale carries a client-generated UUID sent as
 * `client_id`, and the server's unique constraint on (company_id, client_id) is
 * what makes the replay safe: if the first attempt actually reached the server
 * and only the response was lost, the replay resolves to the order that already
 * exists instead of selling the goods twice.
 *
 * Storage and the replay rules live in `sales-queue-store.ts` so the service
 * worker can run the same logic with no tab open. This module adds what only a
 * page can do: status listeners, connectivity reporting, the cross-tab lock,
 * and asking the browser for background sync.
 *
 * Deliberately NOT a general-purpose request queue: only the POS sale endpoint
 * is queued, and a rejection is recorded for a human rather than dropped.
 */

import { isOffline, isSimulatedOffline, reportApiUnreachable } from '@/lib/connectivity'
import { getDeviceId } from '@/lib/device-id'
import { requestPersistentStorage } from '@/lib/storage-policy'
import {
  deleteSale as deleteSaleRecord,
  listSales,
  putSale,
  queuedKindFor,
  replayPendingSales,
  setQueueIdentity,
  setQueueToken,
  type QueuedSale,
  type QueueIdentity,
} from '@/lib/sales-queue-store'

export type { QueuedSale } from '@/lib/sales-queue-store'
export { MAX_REPLAY_ATTEMPTS, newSaleId } from '@/lib/sales-queue-store'

/** Tag the service worker listens for; one flush per tag per registration. */
export const BACKGROUND_SYNC_TAG = 'stockwise-sales'

/** Where the provisional receipt counter lives between sessions. */
const RECEIPT_SEQUENCE_KEY = 'stockwise:offline-receipt-seq'

/**
 * The slip number a customer gets for a sale that has not reached the server.
 *
 * `QS-OFF-<device>-<n>`: the device part keeps two tills from minting the same
 * number, and the counter makes it look like a receipt rather than a UUID. The
 * server's real order number replaces it once the sale syncs.
 */
export function makeProvisionalReceipt(deviceId: string = getDeviceId()): string {
  const device = deviceId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase() || 'TILL'
  let sequence = 1
  try {
    const stored = Number(localStorage.getItem(RECEIPT_SEQUENCE_KEY) ?? '0')
    sequence = Number.isFinite(stored) && stored > 0 ? stored + 1 : 1
    localStorage.setItem(RECEIPT_SEQUENCE_KEY, String(sequence))
  } catch {
    // No storage: the sale still gets a numbered slip, it just may repeat.
    sequence = Date.now() % 10000
  }
  return `QS-OFF-${device}-${String(sequence).padStart(4, '0')}`
}

/**
 * What this unsynced sale is worth, computed the way the till computed it.
 *
 * Quick-sale items are priced per litre and sent as `volume_ml`; a sales order
 * sends a plain `quantity`. This is display-only — the server recomputes the
 * real total — so an unknown shape yields null instead of a wrong number.
 */
export function queuedSaleTotal(body: Record<string, unknown>): number | null {
  const items = body.items
  if (!Array.isArray(items) || items.length === 0) return null

  let total = 0
  for (const raw of items) {
    const item = raw as { volume_ml?: number; quantity?: number; unit_price?: number }
    const price = Number(item.unit_price)
    if (!Number.isFinite(price)) return null

    if (Number.isFinite(Number(item.volume_ml))) total += (Number(item.volume_ml) / 1000) * price
    else if (Number.isFinite(Number(item.quantity))) total += Number(item.quantity) * price
    else return null
  }
  return total
}

export interface QueueStatus {
  pending: number;
  failed: number;
  replaying: boolean;
  /** A replay got a 401/403: the session ended and records cannot be sent. */
  needsSignIn: boolean;
  /** Split by kind, because a repayment is not a sale and must not be counted as one. */
  pendingSales: number;
  pendingPayments: number;
  failedSales: number;
  failedPayments: number;
  /** Queued under another company; held until that account signs in. */
  held: number;
}

const isBrowser = typeof window !== 'undefined';

/** The replay currently in progress, so a logout can cancel it. */
let inFlightFlush: AbortController | null = null;

let status: QueueStatus = {
  pending: 0,
  failed: 0,
  replaying: false,
  needsSignIn: false,
  pendingSales: 0,
  pendingPayments: 0,
  failedSales: 0,
  failedPayments: 0,
  held: 0,
};
const listeners = new Set<(next: QueueStatus) => void>();

function publish(patch: Partial<QueueStatus>): void {
  status = { ...status, ...patch };
  for (const listener of listeners) listener(status);
}

export function getQueueStatus(): QueueStatus {
  return status;
}

export function subscribeQueueStatus(listener: (next: QueueStatus) => void): () => void {
  listeners.add(listener);
  listener(status);
  return () => {
    listeners.delete(listener);
  };
}

/** Every queued sale, oldest first. */
async function listQueuedSales(): Promise<QueuedSale[]> {
  return listSales()
}

export { listQueuedSales }

async function refreshCounts(): Promise<void> {
  const rows = await listSales();
  const isPayment = (row: QueuedSale) => row.kind === 'credit_payment';
  const pending = rows.filter((row) => !row.lastError);
  const failed = rows.filter((row) => Boolean(row.lastError));

  publish({
    pending: pending.length,
    failed: failed.length,
    pendingSales: pending.filter((row) => !isPayment(row)).length,
    pendingPayments: pending.filter(isPayment).length,
    failedSales: failed.filter((row) => !isPayment(row)).length,
    failedPayments: failed.filter(isPayment).length,
    held: rows.filter((row) => Boolean(row.heldReason)).length,
  });
}

/**
 * Who this device is signed in as, used to stamp and gate queued records.
 *
 * Read from the cached session because the queue must work before a network
 * round trip; `stockwise-user` is the older key some screens still write.
 */
export function currentQueueIdentity(): QueueIdentity | null {
  if (!isBrowser) return null;
  for (const key of ['stockwise-user-cache', 'stockwise-user']) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const user = JSON.parse(raw) as { id?: number; company_id?: number };
      const companyId = Number(user?.company_id);
      if (!Number.isFinite(companyId) || companyId <= 0) continue;
      return { companyId, userId: typeof user.id === 'number' ? user.id : null };
    } catch {
      continue;
    }
  }
  return null;
}

/**
 * Ask the browser to replay this queue even if the app is closed.
 *
 * Chrome/Android only: Safari has no Background Sync, so there the queue is
 * drained on app open and on `visibilitychange` instead (see the sync hook).
 * Failure is silent on purpose — it is an optimisation, not a requirement.
 */
function requestBackgroundSync(): void {
  if (!isBrowser || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => {
      const sync = (registration as ServiceWorkerRegistration & {
        sync?: { register: (tag: string) => Promise<void> };
      }).sync;
      return sync?.register(BACKGROUND_SYNC_TAG);
    })
    .catch(() => {
      /* unsupported or registration not ready; the page still flushes */
    });
}

/**
 * Run the replay under a cross-tab lock.
 *
 * Two tabs flushing at once is harmless for the books (the server dedupes) but
 * it doubles every request and makes the two banners disagree. Where `locks` is
 * unavailable the run just goes ahead.
 */
async function withFlushLock(run: () => Promise<void>): Promise<void> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) {
    await run();
    return;
  }
  await locks.request('stockwise:sales-flush', run);
}

/** Queue a sale that could not reach the server. Returns the client id used. */
export async function enqueueSale(
  endpoint: string,
  body: Record<string, unknown>,
  id: string,
  /** When the sale happened, if the caller knows it. Defaults to now. */
  capturedAt: number = Date.now(),
  /** What the slip says the sale is worth, when the caller knows it. */
  total: number | null = null,
): Promise<void> {
  if (!isBrowser) return;
  // The first queued sale is the moment this device starts holding revenue in
  // browser storage, so it is the moment to ask not to be evicted.
  void requestPersistentStorage();

  // The worker cannot read localStorage, so the token and the identity it must
  // replay under are mirrored beside the queue.
  await setQueueToken(localStorage.getItem('token'));
  const identity = currentQueueIdentity();
  await setQueueIdentity(identity);

  // Re-queueing the same sale (a retry, a reload) keeps the number already
  // printed on the customer's slip instead of minting a second receipt.
  const existing = (await listSales()).find((sale) => sale.id === id);

  await putSale({
    id,
    endpoint,
    body,
    createdAt: Date.now(),
    capturedAt,
    kind: queuedKindFor(endpoint),
    receipt: existing?.receipt ?? makeProvisionalReceipt(),
    total: total ?? existing?.total ?? queuedSaleTotal(body) ?? undefined,
    // A re-queued sale keeps the tenant it was originally rung up for; only a
    // brand-new record is stamped with the session active now.
    companyId: existing?.companyId ?? identity?.companyId,
    userId: existing?.companyId ? existing.userId : (identity?.userId ?? null),
    attempts: 0,
    lastError: null,
  });
  await refreshCounts();
  requestBackgroundSync();
}

/**
 * Forget the session used by the queue when the user signs out.
 *
 * The replayed bearer token is kept in IndexedDB so a background sync can post
 * without a tab open — which also means it would outlive the logout that was
 * supposed to end it. Clearing it here means a shared till cannot send one
 * person's queued sales under their still-valid token after they have left;
 * the records stay, and the next sign-in supplies a session again.
 */
export async function clearQueueSession(): Promise<void> {
  // Stop a replay that is already running — including its in-flight request —
  // before the token is dropped. Otherwise a request the signing-out user
  // already started could still complete under their session.
  inFlightFlush?.abort()
  inFlightFlush = null

  // A background sync runs in the service worker, out of reach of the
  // controller above; tell it to stop its own replay too.
  if (isBrowser && 'serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.active?.postMessage({ type: 'CANCEL_SALES_REPLAY' })
      })
      .catch(() => {
        /* no worker yet, so there is no replay to cancel */
      })
  }

  await setQueueToken(null)
  await setQueueIdentity(null)
  // The queue is now waiting for a session, not for the network.
  publish({ needsSignIn: true })
}

/** Replay every pending sale, oldest first. */
export async function flushQueue(): Promise<void> {
  if (!isBrowser || status.replaying) return;
  if (isOffline()) return;
  // Simulated offline (testing) must hold the queue too, otherwise a fake
  // outage would drain it and the replay path would go unchecked.
  if (isSimulatedOffline()) return;

  await withFlushLock(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Signed out: there is no session to authenticate a replay with, and the
      // banner already asks for one. Never post the queue anonymously.
      await clearQueueSession();
      publish({ needsSignIn: true });
      return;
    }

    const controller = new AbortController();
    inFlightFlush = controller;
    publish({ replaying: true });
    try {
      const identity = currentQueueIdentity();
      await setQueueToken(token);
      await setQueueIdentity(identity);

      const outcome = await replayPendingSales({
        token,
        identity,
        signal: controller.signal,
        fetchImpl: (input, init) => fetch(input, init),
      });

      if (outcome.unreachable) reportApiUnreachable();
      // A logout during the run also means "sign in again", even though the
      // interrupted replay itself saw no 401.
      publish({ needsSignIn: outcome.needsSignIn || !localStorage.getItem('token') });
    } finally {
      if (inFlightFlush === controller) inFlightFlush = null;
      publish({ replaying: false });
      await refreshCounts();
    }
  });
}

/** Retry a sale that was marked failed, after a human has looked at it. */
export async function retryFailedSale(id: string): Promise<void> {
  const rows = await listSales();
  const sale = rows.find((row) => row.id === id);
  if (!sale) return;
  await putSale({ ...sale as QueuedSale, attempts: 0, lastError: null });
  await refreshCounts();
  await flushQueue();
}

/** Drop a queued sale. Only for failures a human has judged unrecoverable. */
export async function discardSale(id: string): Promise<void> {
  await deleteSaleRecord(id);
  await refreshCounts();
}

/** Re-count on demand, e.g. when the status UI mounts. */
export async function refreshQueueCounts(): Promise<void> {
  await refreshCounts();
}
